import { ROUTES } from "../routes.js";
import { clearAllCaches, post } from "./client";
import { type Nominal, type OkResponse, url } from "./common";

// ---- IDs ----

export type UserId = Nominal<string, "User">;
export type AnonUserId = Nominal<string, "AnonUser">;
export type IdentityId = Nominal<string, "Identity">;
export type InviteId = Nominal<string, "Invite">;
export type PendingInviteId = Nominal<string, "PendingInvite">;

// ---- Entity ----

export type ProfileImageData = { path: string | null };

export type PeertubeUserInfo = {
  username: string;
  password: string;
  id: number;
  channelId: number;
};

export type User = {
  id: UserId;
  name: string;
  tag: number;
  email: string | null;
  isAdmin: boolean;
  profileImageData: ProfileImageData | null;
  createdAt: Date;
  lastActionAt: Date;
  conversionChannel: string | null;
  peertubeUserInfo: PeertubeUserInfo | null;
};

// Subset of User returned by extractUserProps() — the public shape the API exposes
// for an authenticated identity. Used by /auth/me, /auth/login, /auth/register,
// invite-apply, and reset-token endpoints.
export type AuthenticatedUserInfo = {
  id: UserId;
  name: string;
  tag: number;
  email: string | null;
  isAdmin: boolean;
  profileImageData: ProfileImageData | null;
  lastActionAt: Date;
  conversionChannel: string | null;
};

// Minimal user summary, used by populated relations (e.g. WorkshopInfo.owner).
export type UserSummary = {
  id: UserId;
  name: string;
};

// ---- Response shapes ----

// Response of GET /auth/me. The endpoint returns null when no identity exists
// (in addition to the fetcher mapping HTTP 401 to null).
export type MeResponse =
  | ({ isAnon: false } & AuthenticatedUserInfo)
  | { isAnon: true; id: AnonUserId; name: string }
  | null;

// Common shape returned by login/register/applyInvite/etc.
export type AuthSuccessResponse = {
  ok: true;
  user: AuthenticatedUserInfo;
};

// Returned by GET /auth/invites/:secret.
export type InviteInfo = {
  ok: true;
  role: "teacher" | "student";
  alreadyAccepted: boolean;
};

// Returned by GET /auth/reset-token/:token.
export type ResetPwTokenInfo = {
  ok: true;
  user: AuthenticatedUserInfo;
};

// Item in GET /auth/admin response array.
export type AdminUserRow = {
  id: UserId;
  name: string;
  tag: number;
  isAdmin: boolean;
  createdAt: Date;
  profileImageData: ProfileImageData | null;
  lastActionAt: Date;
};

// Returned by POST /users/:userId/create-password-reset-link.
export type CreateResetPasswordLinkResponse = {
  url: string;
};

// ---- Models ----

export const userModels = {
  ME: url<MeResponse>(`/auth/me`),
  INVITE: (secret: string) => url<InviteInfo>(`/auth/invites/${secret}`),
  RESET_PW_TOKEN_INFO: (token: string) =>
    url<ResetPwTokenInfo>(`/auth/reset-token/${token}`),

  // Admin Area
  ADMIN_USERS: url<AdminUserRow[]>(`/auth/admin`),
};

// ---- Mutations ----

type LoginInput = {
  password: string;
  username?: string;
  tag?: string;
  email?: string;
};

type RegisterInput = {
  username: string;
  email: string;
  password: string;
  sendNewsletter: boolean | null;
};

type ApplyInviteInput = {
  secret: string;
  password: string;
  username: string;
  email?: string;
  sendNewsletter?: boolean;
};

// Same handler as ApplyInviteInput with ?addToSmallTeam=true; takes `name` (mapped to `username`).
type ApplyInviteAndAddUserToNewSmallTeamInput = {
  secret: string;
  password: string;
  name: string;
  email?: string;
};

type UpdateUserDetailsInput = {
  name?: string;
  email?: string;
  conversionChannel?: string;
};

export const userMutations = {
  login: ({ username, tag, password, email }: LoginInput) =>
    post<AuthSuccessResponse>(
      "/auth/login",
      { username, tag, password, email },
      [userModels.ME],
    ).then(async (result) => {
      await clearAllCaches();
      window.location.reload();
      return result;
    }),
  logout: () =>
    post<OkResponse>("/auth/logout", {}, [userModels.ME]).then(
      async (result) => {
        await clearAllCaches();
        window.location.href = ROUTES.login.getUrl();
        return result;
      },
    ),
  register: ({ username, email, password, sendNewsletter }: RegisterInput) =>
    post<AuthSuccessResponse>(
      `/auth/register`,
      { username, email, password, sendNewsletter },
      [userModels.ME],
    ),

  resetPasswordRequest: ({ email }: { email: string }) =>
    post<OkResponse>(`/auth/reset-password-request`, { email }, [
      userModels.ME,
    ]),
  createResetPasswordLink: ({ userId }: { userId: UserId }) =>
    post<CreateResetPasswordLinkResponse>(
      `/users/${userId}/create-password-reset-link`,
      {},
      [],
    ),
  resetPassword: ({ token, password }: { token: string; password: string }) =>
    post<OkResponse>(`/auth/reset-password/${token}`, { password }, [
      userModels.ME,
    ]),

  applyInvite: ({
    secret,
    password,
    username,
    email,
    sendNewsletter,
  }: ApplyInviteInput) =>
    post<AuthSuccessResponse>(
      `/auth/invites/${secret}/apply`,
      { password, username, email, sendNewsletter },
      [userModels.ME],
    ),
  applyInviteAndAddUserToNewSmallTeam: ({
    secret,
    password,
    name,
    email,
  }: ApplyInviteAndAddUserToNewSmallTeamInput) =>
    post<AuthSuccessResponse>(
      `/auth/invites/${secret}/apply?addToSmallTeam=true`,
      { password, username: name, email },
      [userModels.ME], // This would also update the workshopModels.WORKSHOP_USER_INFO(workshopId), smallTeamModels.WORKSHOP_SMALL_TEAMS(workshopId) but we don't have workshopId.
    ),
  applyInviteToAuthUser: ({ secret }: { secret: string }) =>
    post<OkResponse>(`/auth/invites/${secret}/apply-existing-user`, {}, []),
  makeAdmin: ({ email }: { email: string }) =>
    post<OkResponse>(`/auth/admin`, { email }, [userModels.ADMIN_USERS]),
  unmakeAdmin: ({ id }: { id: UserId }) =>
    post<OkResponse>(`/auth/admin/${id}/delete`, {}, [userModels.ADMIN_USERS]),
  updateProfileImage: ({
    profileImageData,
  }: {
    profileImageData: ProfileImageData | null;
  }) =>
    post<OkResponse>("/auth/update-profile-image", { profileImageData }, [
      userModels.ME,
    ]),
  updatePassword: ({
    oldPassword,
    newPassword,
  }: {
    oldPassword: string;
    newPassword: string;
  }) =>
    post<OkResponse>("/auth/update-password", { oldPassword, newPassword }, []),
  updateUserDetails: ({
    name,
    email,
    conversionChannel,
  }: UpdateUserDetailsInput) =>
    post<OkResponse>(
      "/auth/update-user-details",
      { name, email, conversionChannel },
      [userModels.ME],
    ),
  deleteMe: () =>
    post<OkResponse>("/user/delete", {}, [userModels.ME]).then(
      async (result) => {
        await clearAllCaches();
        window.location.href = ROUTES.login.getUrl();
        return result;
      },
    ),
};
